import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import AsyncTable, { Column, RequestPage } from '../../../components/AsyncTable';
import Btn from '../../../components/Buttons/Btn';
import NewRegisterBtn from '../../../components/Buttons/NewRegisterBtn';
import Modal from '../../../components/Modal';
import SelectAsync from '../../../components/SelectAsync';
import {
  ActionButtonsContainer,
  EditIconTable,
  ViewIconTable,
  SaveIconTable,
  CancelIconTable,
  MdFileCopyIcon,
} from '../../../components/TablePaginator/styles';
import ContentContainer from '../../../templates/Content/ContentContainer';
import { getApi } from '../../../services/api';
import ApiCore from '../../../services/utilities/core';
import { backendErrorNotification } from '../../../components/Notification';
import checkPermission from '../../../utils/checkPermission';
import requireSuccessfulResponse from '../../../services/utilities/requireSuccessfulResponse';
import * as s from './styles';
import NotificationDateInput from './Components/NotificationDateInput';

type Option = { id: string | number; label: string };
type Target = {
  mode: string;
  user_ids?: number[];
  seller_codes?: string[];
  audience_ids?: number[];
  tenant_ids?: number[];
};
type Message = {
  id?: number;
  created_at?: string;
  title: string;
  body: string;
  type: string;
  origin: string;
  priority: boolean;
  required_for_order: boolean;
  action_url: string;
  order_number: string;
  scheduled_at: string;
  expires_at: string;
  status: string;
  target: Target;
  data?: Record<string, unknown>;
  recipients_count?: number;
  read_count?: number;
  seen_count?: number;
  tenant_id?: number;
  tenant_name?: string;
};
type TenantInfo = { tenant_id: number; tenant_name: string };
type DeliveryResult = TenantInfo & { id: number | null; success: boolean; status: string | null };
const notificationCategories = ['system', 'marketing', 'order_update'] as const;
const targetKeys: Record<string, string> = {
  users: 'user_ids', sellers: 'seller_codes', audiences: 'audience_ids', tenants: 'tenant_ids',
};
const notificationOrigin = 'SmartsellAdmin';
const blank = (): Message => ({
  title: '',
  body: '',
  type: 'system',
  origin: notificationOrigin,
  priority: false,
  required_for_order: false,
  action_url: '',
  order_number: '',
  scheduled_at: '',
  expires_at: '',
  status: 'draft',
  target: { mode: 'all' },
});
function localDate(value: string | null | undefined) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function engagement(count = 0, total = 0) {
  return `${count} (${total > 0 ? Math.round((count / total) * 100) : 0}%)`;
}
export default function Notifications({ superadmin = false }: { superadmin?: boolean }) {
  const { t, i18n } = useTranslation();
  const tr = (key: string) => t(`notify.${key}`);
  const endpoint = superadmin ? '/api/v1/superadmin/notifications' : '/api/v1/notifications';
  const can = (action: string) => superadmin || checkPermission(`notification-${action}`);
  const rowPath = (row: Message) => superadmin ? `/tenants/${row.tenant_id}/${row.id}` : `/${row.id}`;
  async function call(method: 'get' | 'post' | 'put', path = '', data?: unknown) {
    const api = await getApi();
    const response = method === 'get' ? await api.get(endpoint + path) : await api[method](endpoint + path, data);
    requireSuccessfulResponse(response);
    return response.data;
  }
  const [tenantFilter, setTenantFilter] = useState<Option | null>(null);
  const [unavailableTenants, setUnavailableTenants] = useState<TenantInfo[]>([]);
  const [deliveryResults, setDeliveryResults] = useState<DeliveryResult[] | null>(null);
  const [rows, setRows] = useState<Message[]>([]);
  const theme = useTheme();
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<Message | null>(null);
  const [extraData, setExtraData] = useState('');
  let parsedData: Record<string, unknown> | null = null;
  let invalidData = false;
  if (extraData.trim()) {
    try {
      const value = JSON.parse(extraData);
      if (!value || typeof value !== 'object' || Array.isArray(value)) invalidData = true;
      else parsedData = value;
    } catch {
      invalidData = true;
    }
  }
  const [chosen, setChosen] = useState<Option[]>([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{
    total: number;
    users: { id: number; name: string }[];
  } | null>(null);
  const [report, setReport] = useState<any>(null);
  const [reportItem, setReportItem] = useState<Message | null>(null);
  const [reportPage, setReportPage] = useState(1);
  const [resendItem, setResendItem] = useState<Message | null>(null);
  const [cancelItem, setCancelItem] = useState<Message | null>(null);
  const mode = form?.target.mode;
  const allowedKinds = superadmin ? ['users', 'tenants'] : ['users', 'sellers', 'audiences'];
  const kind = mode && allowedKinds.includes(mode) ? mode : null;
  const modeLabel = (value: string) => {
    if (superadmin && value === 'all') return tr(form?.id ? 'tenantUsers' : 'allTenantUsers');
    return tr(value);
  };
  const recipientsRequest = useMemo(() => {
    const request = new ApiCore({ url: `${endpoint}/options` });
    request.index = async () => {
      const api = await getApi();
      const response = await api.get(`${endpoint}/options${request.query}`);
      requireSuccessfulResponse(response);
      return { ...response, data: { ...response.data, data: response.data.data.data } };
    };
    return request;
  }, [endpoint]);
  const tenantsRequest = useMemo(() => {
    const request = new ApiCore({ url: `${endpoint}/options` });
    request.index = async () => {
      const api = await getApi();
      const response = await api.get(`${endpoint}/options${request.query}`);
      requireSuccessfulResponse(response);
      return { ...response, data: { ...response.data, data: response.data.data.data } };
    };
    return request;
  }, [endpoint]);
  useEffect(() => {
    if (!reportItem) return;
    let alive = true;
    void call('get', `${rowPath(reportItem)}?page=${reportPage}`)
      .then((r) => {
        if (alive) setReport(r.data);
      })
      .catch(backendErrorNotification);
    return () => {
      alive = false;
    };
  }, [reportItem, reportPage, endpoint]);
  function field(key: keyof Message, value: unknown) {
    setForm((current) => (current ? { ...current, [key]: value } : null));
    setPreview(null);
  }
  function target(nextMode: string, values: Option[] = []) {
    const key = targetKeys[nextMode];
    field('target', {
      mode: nextMode,
      ...(['tenants', 'users', 'sellers', 'audiences'].includes(nextMode)
        ? { [key]: values.map((o) => (nextMode === 'sellers' ? String(o.id) : Number(o.id))) }
        : {}),
    });
  }
  async function perform(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      setReload((n) => n + 1);
    } catch (e) {
      backendErrorNotification(e as any);
    } finally {
      setBusy(false);
    }
  }
  function open(row?: Message) {
    const draft = row
      ? {
        ...row,
        action_url: row.action_url || '',
        order_number: row.order_number || '',
        scheduled_at: localDate(row.scheduled_at),
        expires_at: localDate(row.expires_at),
      }
      : blank();
    const ids =
      draft.target.tenant_ids || draft.target.user_ids || draft.target.seller_codes || draft.target.audience_ids || [];
    setForm(draft);
    setExtraData(draft.data ? JSON.stringify(draft.data, null, 2) : '');
    setChosen(ids.map((id) => ({ id, label: String(id) })));
    setPreview(null);
  }
  async function save(publish: boolean) {
    if (!form || invalidData || !notificationCategories.some((category) => category === form.type)) return;
    const payload = {
      ...form,
      origin: notificationOrigin,
      data: parsedData,
      action_url: form.action_url || null,
      order_number: form.order_number || null,
      scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      status: publish && form.scheduled_at ? 'scheduled' : 'draft',
    };
    if (superadmin && !form.id) {
      const result = await call('post', '', { ...payload, publish });
      setDeliveryResults(result.data.results);
      setForm(null);
      return;
    }
    const result = await call(form.id ? 'put' : 'post', form.id ? rowPath(form) : '', payload);
    // Se o envio falhar depois da criação, a próxima tentativa reutiliza o mesmo rascunho.
    setForm((current) =>
      current ? { ...current, id: result.data.id, status: result.data.status } : null,
    );
    if (publish && !form.scheduled_at) await call('post', `${rowPath({ ...form, id: result.data.id })}/publish`);
    setForm(null);
  }
  const tableStyles = {
    primaryColor: theme.colors.surface,
    secondaryColor: theme.colors.onSurface,
    alternateRowColor: theme.colors.onSurfaceLight,
    textColor: theme.colors.text,
  };
  // A API usa páginas de 20 registros; a tabela permite escolher outra quantidade.
  const requestPage: RequestPage = async ({ search, page, quantityPerPage }) => {
    try {
      const offset = (page - 1) * quantityPerPage;
      const firstPage = Math.floor(offset / 20) + 1;
      const lastPage = Math.ceil((offset + quantityPerPage) / 20);
      const tenantQuery = tenantFilter ? `&tenant_id=${tenantFilter.id}` : '';
      const results = await Promise.all(
        Array.from({ length: lastPage - firstPage + 1 }, (_, index) =>
          call('get', `?page=${firstPage + index}&search=${encodeURIComponent(search)}${tenantQuery}`),
        ),
      );
      const totalPage = Math.ceil(results[0].paginated.totalItems / quantityPerPage);
      if (superadmin) {
        const unique = new Map<number, TenantInfo>();
        results.flatMap((result) => result.unavailable_tenants || []).forEach((tenant: TenantInfo) => {
          unique.set(tenant.tenant_id, tenant);
        });
        setUnavailableTenants(Array.from(unique.values()));
      }
      return {
        data: results
          .flatMap((result) => result.data)
          .slice(offset % 20, (offset % 20) + quantityPerPage),
        totalPage,
        hasMore: page < totalPage,
      };
    } catch (error) {
      backendErrorNotification(error as any);
      return { data: [], totalPage: 0, hasMore: false };
    }
  };
  const columns: Column<Message>[] = [
    { label: tr('status'), Cell: (row) => tr(row.status) },
    ...(superadmin ? [{ label: t('tenant'), accessor: 'tenant_name' as const }] : []),
    { label: tr('fieldTitle'), accessor: 'title' },
    { label: tr('createdAt'), Cell: (row) => row.created_at
      ? new Date(row.created_at).toLocaleString(i18n.language, {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
      }) : '' },
    { label: tr('required'), Cell: (row) => (row.required_for_order ? t('yes') : t('no')) },
    { label: tr('recipients'), accessor: 'recipients_count' },
    { label: tr('seen'), Cell: (row) => engagement(row.seen_count, row.recipients_count) },
    { label: tr('read'), Cell: (row) => engagement(row.read_count, row.recipients_count) },
    {
      label: tr('actions'),
      Cell: (row) => (
        <ActionButtonsContainer>
          <ViewIconTable
            title={tr('details')}
            onClick={() => {
              setReport(null);
              setReportPage(1);
              setReportItem(row);
            }}
          />
          {['draft', 'scheduled'].includes(row.status) && can('edit') && (
            <EditIconTable title={tr('edit')} onClick={() => !busy && open(row)} />
          )}
          {['draft', 'scheduled'].includes(row.status) && can('send') && (
            <SaveIconTable
              title={tr('publish')}
              onClick={() =>
                !busy &&
                void perform(async () => {
                  await call('post', `${rowPath(row)}/publish`);
                })
              }
            />
          )}
          {can('create') && can('send') && (
            <MdFileCopyIcon
              title={tr('copyAndResend')}
              onClick={() => !busy && setResendItem(row)}
            />
          )}
          {row.status !== 'cancelled' && can('cancel') && (
            <CancelIconTable title={tr('cancel')} onClick={() => !busy && setCancelItem(row)} />
          )}
        </ActionButtonsContainer>
      ),
    },
  ];
  const validCategory = notificationCategories.some((category) => category === form?.type);
  const invalid = busy || invalidData || !validCategory || !form?.title.trim() || !form?.body.trim();
  const closeForm = () => {
    if (!busy) setForm(null);
  };
  const textField = (
    key:
      | 'title'
      | 'body'
      | 'order_number'
      | 'action_url'
      | 'scheduled_at'
      | 'expires_at',
    label: string,
  ) => (
    <div>
      <s.Label htmlFor={`notification-${key}`}>{tr(label)}</s.Label>
      {key === 'body' ? (
        <textarea
          id={`notification-${key}`}
          className="input"
          rows={4}
          maxLength={16000}
          value={form?.body || ''}
          onChange={(event) => field(key, event.target.value)}
        />
      ) : key === 'scheduled_at' || key === 'expires_at' ? (
        <NotificationDateInput id={`notification-${key}`} label={tr(label)}
          value={form?.[key] || ''} onChange={(value) => field(key, value)} disabled={busy} />
      ) : (
        <input
          id={`notification-${key}`}
          className="input"
          type="text"
          maxLength={key === 'title' ? 255 : undefined}
          value={form?.[key] || ''}
          onChange={(event) => field(key, event.target.value)}
        />
      )}
    </div>
  );
  return (
    <ContentContainer title={tr('title')}>
      {can('create') && (
        <NewRegisterBtn onClick={() => open()} style={{ marginBottom: '1rem', marginLeft: 0 }} />
      )}
      {superadmin && <div style={{ maxWidth: 420, marginBottom: '1rem' }}>
        <s.Label htmlFor="notification-tenant-filter">{tr('filterTenant')}</s.Label>
        <SelectAsync id="notification-tenant-filter" request={tenantsRequest} query="kind=tenants"
          value={tenantFilter ? { value: tenantFilter.id, label: tenantFilter.label } : null}
          placeholder={tr('allTenants')} fixedOptions={[{ value: '', label: tr('allTenants') }]}
          reqResponseToOption={{ mapper: { value: (option: Option) => option.id, label: (option: Option) => option.label } }}
          onChange={(option: { value: number; label: string } | null) => {
            setTenantFilter(option?.value ? { id: option.value, label: option.label } : null);
          }} />
      </div>}
      {!!unavailableTenants.length && <s.Notice role="alert">
        {tr('unavailableTenants')}: {unavailableTenants.map((tenant) => tenant.tenant_name).join(', ')}
      </s.Notice>}
      {deliveryResults && <s.Notice role="status">
        <strong>{tr(deliveryResults.every((result) => result.success) ? 'batchSaved' : 'batchPartial')}</strong>
        <ul>{deliveryResults.map((result) => <li key={result.tenant_id}>
          {result.tenant_name}: {result.success
            ? tr(result.status || 'draft') : tr(result.id ? 'batchDraftFailed' : 'batchFailed')}
        </li>)}</ul>
        <Btn type="text" text={tr('close')} onClick={() => setDeliveryResults(null)} />
      </s.Notice>}
      <AsyncTable
        key={String(tenantFilter?.id ?? 'all')}
        tableName={tr('title')}
        columns={columns}
        value={rows}
        onChange={setRows}
        requestPage={requestPage}
        reqListener={[reload, tenantFilter?.id]}
        defaultQuantityPerPage={20}
        options={{
          styles: tableStyles,
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
      {!!form && (
        <Modal
          title={tr(form.id ? 'edit' : 'new')}
          showModal
          closeModal={closeForm}
          cancelButton={closeForm}
          hideActionButton
          isLoading={busy}
          disableActionBtn={invalid}
          additionalActions={[
            {
              name: tr('saveDraft'), type: 'outlined',
              onClick: () => void perform(() => save(false)), disable: invalid,
              style: { maxWidth: 'min(170px, 29vw)' },
            },
            ...(can('send') ? [{
              name: tr(form.scheduled_at ? 'schedule' : 'publish'),
              onClick: () => void perform(() => save(true)), disable: invalid,
              style: { maxWidth: 'min(170px, 29vw)' },
            }] : []),
          ]}
          bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem', boxSizing: 'border-box' }}
        >
          <s.FormContent>
            {superadmin && form.tenant_name && <s.Notice>{t('tenant')}: {form.tenant_name}</s.Notice>}
            {textField('title', 'fieldTitle')}
            {textField('body', 'body')}
            <s.Grid>
              <div>
                <s.Label htmlFor="notification-type">{tr('type')}</s.Label>
                <select id="notification-type" className="input" value={validCategory ? form.type : ''}
                  disabled={busy} onChange={(event) => field('type', event.target.value)}>
                  {!validCategory && <option value="" disabled>{tr('selectCategory')}</option>}
                  {notificationCategories.map((category) => (
                    <option key={category} value={category}>{tr(`categories.${category}`)}</option>
                  ))}
                </select>
              </div>
              {textField('order_number', 'order')}
              {textField('action_url', 'link')}
            </s.Grid>
            <s.HelpText>{tr('linkHint')}</s.HelpText>
            <details>
              <summary>{tr('integrationData')}</summary>
              <s.Label htmlFor="notification-data">{tr('data')}</s.Label>
              <textarea
                id="notification-data"
                className="input"
                rows={3}
                value={extraData}
                aria-invalid={invalidData}
                onChange={(event) => setExtraData(event.target.value)}
              />
              <s.HelpText role={invalidData ? 'alert' : undefined}>
                {tr(invalidData ? 'invalidData' : 'dataHint')}
              </s.HelpText>
            </details>
            {(['priority', 'required_for_order'] as const).map((key) => (
              <s.CheckLabel key={key}>
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(event) => field(key, event.target.checked)}
                />
                {tr(key === 'priority' ? 'priority' : 'required')}
              </s.CheckLabel>
            ))}
            <s.HelpText>{tr('requiredHint')}</s.HelpText>
            <s.Grid>
              {textField('scheduled_at', 'scheduled_at')}
              {textField('expires_at', 'expires_at')}
            </s.Grid>
            <div>
              <s.Label htmlFor="notification-target">{tr('recipients')}</s.Label>
              <select
                id="notification-target"
                className="input"
                value={mode}
                onChange={(event) => {
                  setChosen([]);
                  target(event.target.value);
                }}
              >
                {superadmin && form.id && !['all', 'users'].includes(mode || '') && (
                  <option value={mode} disabled>{tr(mode!)}</option>
                )}
                {(superadmin ? (form.id ? ['all', 'users'] : ['all', 'tenants', 'users'])
                  : ['all', 'users', 'sellers', 'seller_users', 'all_sellers', 'audiences']).map(
                  (value) => (
                    <option key={value} value={value}>
                      {modeLabel(value)}
                    </option>
                  ),
                )}
              </select>
              <s.HelpText>
                {tr(superadmin ? (form.id ? 'editTenantHint' : 'superadminRecipientHint') : 'recipientHint')}
              </s.HelpText>
            </div>
            {kind && (
              <div>
                <s.Label>{tr('selectRecipients')}</s.Label>
                <SelectAsync
                  request={recipientsRequest}
                  query={`kind=${kind}${superadmin && form.tenant_id ? `&tenant_id=${form.tenant_id}` : ''}`}
                  cacheUniqs={[kind, form.tenant_id]}
                  isMulti
                  placeholder={tr('selectRecipients')}
                  value={chosen.map((option) => ({ value: option.id, label: option.label }))}
                  reqResponseToOption={{
                    mapper: {
                      value: (option: Option) => option.id,
                      label: (option: Option) => `${option.label} (${option.id})`,
                    },
                  }}
                  onChange={(values: { value: string | number; label: string }[]) => {
                    const next = values.map((value) => ({ id: value.value, label: value.label }));
                    setChosen(next);
                    target(mode!, next);
                  }}
                />
              </div>
            )}
            <Btn
              type="outlined"
              text={tr('preview')}
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  const result = await call('post', '/preview', {
                    target: form.target,
                    ...(superadmin && form.tenant_id ? { tenant_id: form.tenant_id } : {}),
                  });
                  setPreview(result.data);
                })
              }
            />
            {preview && (
              <s.Notice role="status">
                {tr('total')}: {preview.total}
                <br />
                {preview.users.map((user) => user.name).join(', ')}
              </s.Notice>
            )}
          </s.FormContent>
        </Modal>
      )}
      {!!resendItem && (
        <Modal
          title={tr('copyAndResend')}
          showModal
          closeModal={() => !busy && setResendItem(null)}
          cancelButton={() => !busy && setResendItem(null)}
          actionNameBtn={tr('confirm')}
          isLoading={busy}
          disableActionBtn={busy}
          action={() => {
            if (busy) return;
            void perform(async () => {
              await call('post', `${rowPath(resendItem)}/copy-and-resend`);
              setResendItem(null);
            });
          }}
        >
          <s.Confirmation as="div">
            <strong>{resendItem.title}</strong>
            {superadmin && resendItem.tenant_name && <p>{t('tenant')}: {resendItem.tenant_name}</p>}
            <p>{tr('copyAndResendHint')}</p>
          </s.Confirmation>
        </Modal>
      )}
      {!!cancelItem && (
        <Modal
          title={tr('cancel')}
          showModal
          closeModal={() => !busy && setCancelItem(null)}
          cancelButton={() => !busy && setCancelItem(null)}
          actionNameBtn={tr('confirm')}
          isLoading={busy}
          disableActionBtn={busy}
          action={() =>
            void perform(async () => {
              await call('post', `${rowPath(cancelItem)}/cancel`);
              setCancelItem(null);
            })
          }
        >
          <s.Confirmation>{tr('cancelHint')}</s.Confirmation>
        </Modal>
      )}
      {!!reportItem && (
        <Modal
          title={report?.notification.title || tr('details')}
          showModal
          onlyCloseButton
          closeModal={() => setReportItem(null)}
          bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem', boxSizing: 'border-box' }}
        >
          <s.FormContent>
            {!report ? (
              t('loading')
            ) : (
              <>
                {superadmin && <strong>{report.tenant_name}</strong>}
                <s.MessageBody>{report.notification.body}</s.MessageBody>
                <div>
                  {tr('status')}: {tr(report.notification.status)}
                </div>
                <AsyncTable
                  tableName={tr('recipients')}
                  value={report.recipients.data}
                  onChange={() => undefined}
                  columns={[
                    { label: tr('user'), accessor: 'user_name' },
                    ...['seen_at', 'read_at', 'deleted_at'].map((key, index) => ({
                      label: tr(['seen', 'read', 'deleted'][index]),
                      Cell: (row: any) => (row[key] ? new Date(row[key]).toLocaleString() : '—'),
                    })),
                  ]}
                  requestPage={async () => ({
                    data: report.recipients.data,
                    totalPage: 1,
                    hasMore: false,
                  })}
                  reqListener={[report]}
                  options={{
                    styles: tableStyles,
                    hideController: true,
                    hideSearchAndDownloadArea: true,
                  }}
                />
                <s.Pagination>
                  <Btn
                    type="text"
                    text={tr('previous')}
                    disabled={reportPage <= 1}
                    onClick={() => setReportPage((page) => page - 1)}
                  />
                  <span>
                    {reportPage} / {report.recipients.last_page || 1}
                  </span>
                  <Btn
                    type="text"
                    text={tr('next')}
                    disabled={reportPage >= report.recipients.last_page}
                    onClick={() => setReportPage((page) => page + 1)}
                  />
                </s.Pagination>
              </>
            )}
          </s.FormContent>
        </Modal>
      )}
    </ContentContainer>
  );
}
